import './OrderDetails.css';
import React from 'react';
import { useRemoteData } from '../../../../model/data';
import { Container } from '../../../../components/Container/Container';
import { useMeta, getPageTitle } from '../../../../model/meta';
import { loadOrderWithItemGroups, isClearingOrder } from '../../../../model/order';
import { useRouterParam } from '../../../../model/history';
import { LoadingPage } from '../../../../components/LoadingPage/LoadingPage';
import { ordersPath } from '../../../../routes';
import { Activities } from '../../../../components/Activities/Activities';
import { GeneralInfo } from './GeneralInfo';
import { OrderItems } from './OrderItems';
import { UserDetails } from './UserDetails';
import { ShopDetails } from './shop_details';
import { VoucherDetails } from './voucher_details';
import { t } from '../../../../locales/translate';
import { smoothScrollToElement, useReloadKey } from '../../../../util/util';
import { TicketRefundTool } from './OrderItemsRow/RefundTool';
import { Grid } from '../../../../components/Grid/Grid';
import { Stack } from '../../../../components/Stack/Stack';
import { compact, uniqBy } from 'lodash';
import { highlightElements } from '../../../../util/ui-helpers';
import { Product } from '../../../../model/product';
import { ProductImportantActivities } from '../../../../components/Activities/ProductImportantActivities';

function highlightOrderItem(sku?: string): void {
  if (sku) {
    smoothScrollToElement('order-items-anchor');
    highlightElements(`.product-sku-${sku}`);
  }
}

export function OrderDetails(): JSX.Element {
  const { id } = useRouterParam<{ id: string }>();
  const [key, reload] = useReloadKey();
  const [activitiesKey, reloadActivities] = useReloadKey();

  const [order, loading, error] = useRemoteData(
    async () => {
      await loadOrderWithItemGroups(id);
    },
    (db) => db.table('orders').find(id),
    [id, key.toString()]
  );

  const isClearing = (order && isClearingOrder(order)) ?? false;

  useMeta({
    title: getPageTitle(loading, `${t.model.order._name} ${order && order.reference}`, order),
    breadcrumbs: [
      { title: t.model.order._plural, url: ordersPath() },
      {
        title: getPageTitle(loading && !order, order?.reference ?? '', order),
      },
    ],
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.order._name)} />;
  } else if (error || !order) {
    return <Container>{t.errors.itemNotFound(t.model.order._name)}</Container>;
  } else if (!isClearing && !order.itemGroups) {
    return <Container>{t.errors.itemNotFound(t.model.order._name)}</Container>;
  }

  const productsInGroup = uniqBy(
    compact<Partial<Product> & { groupId: string }>(
      order.itemGroups?.map((group) => ({ ...group.product, groupId: group.id }))
    ),
    'id'
  );

  return (
    <Container className="order-details">
      <Stack size="lg">
        <GeneralInfo order={order} onRefresh={reload} onReloadActivities={reloadActivities} />
        {!isClearing && (
          <>
            <ProductImportantActivities
              activitiesComponentClassName="order-product-important-activities"
              products={productsInGroup}
              onProductLinkClick={highlightOrderItem}
            />
            <OrderItems anchorId="order-items-anchor" order={order} onChange={reloadActivities} />
          </>
        )}
        <Grid>
          <UserDetails order={order} />
          <VoucherDetails orderId={order.id} />
          <TicketRefundTool order={order} />
          <ShopDetails orderId={order.id} />
        </Grid>
        <Activities subjectType="order" subjectId={order.id} cacheKey={activitiesKey} />
      </Stack>
    </Container>
  );
}
